import {
  getTypeOfFishingOptionsForWay,
  WAY_OF_FISHING_OPTIONS,
} from "../../../utils/catchMetadataOptions";
import {
  formatCoords,
  normalizeCoords,
} from "../../CreatePostModal/catch-form/CatchFormValidation";
import { parseTripDate } from "../pastTripAnalytics";

export function trimToUndefined(value) {
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  return text || undefined;
}

export function toIsoDateString(dateObj) {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatPastTripCoordinates(lat, lng) {
  return formatCoords(lat, lng);
}

function normalizePastTripCoordinateText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function hasValidNormalizedCoordinatePair(value) {
  const normalized = normalizePastTripCoordinateText(value);
  if (!normalized) return false;

  const parts = normalized.split(",");
  if (parts.length !== 2) return false;

  return parts.every((part) => Number.isFinite(Number(part)));
}

export function normalizePastTripCoordinatesInput(value) {
  const raw = normalizePastTripCoordinateText(value);
  if (!raw) {
    return { value: "", isValid: true };
  }

  const createPostNormalized = normalizeCoords(raw);
  if (hasValidNormalizedCoordinatePair(createPostNormalized)) {
    return { value: createPostNormalized, isValid: true };
  }

  const decimalMatches = raw.match(/[-+]?(?:\d+(?:\.\d*)?|\.\d+)/g) || [];
  if (decimalMatches.length >= 2) {
    const formatted = formatCoords(decimalMatches[0], decimalMatches[1]);
    if (formatted) {
      return { value: formatted, isValid: true };
    }
  }

  return { value: raw, isValid: false };
}

export function normalizePastTripOptionLabel(value) {
  if (typeof value !== "string") return "";
  return value.trim().replace(/\s+/g, " ");
}

export function normalizePastTripOptionKey(value) {
  return normalizePastTripOptionLabel(value).toLowerCase();
}

export function findCaseInsensitiveOptionKey(source, value) {
  if (!source || typeof source !== "object") return null;

  const normalizedValue = normalizePastTripOptionKey(value);
  if (!normalizedValue) return null;

  return (
    Object.keys(source).find(
      (key) => normalizePastTripOptionKey(key) === normalizedValue
    ) || null
  );
}

export function dedupeStringValues(values = []) {
  const seen = new Set();
  const deduped = [];

  values.forEach((value) => {
    const normalized = normalizePastTripOptionLabel(value);
    if (!normalized) return;

    const key = normalized.toLowerCase();
    if (seen.has(key)) return;

    seen.add(key);
    deduped.push(normalized);
  });

  return deduped;
}

export function appendCurrentSelectOption(options = [], currentValue = "") {
  const deduped = dedupeStringValues(options);
  const normalizedCurrent = normalizePastTripOptionLabel(currentValue);

  if (!normalizedCurrent) {
    return deduped;
  }

  if (
    deduped.some(
      (option) => normalizePastTripOptionKey(option) === normalizedCurrent.toLowerCase()
    )
  ) {
    return deduped;
  }

  return [normalizedCurrent, ...deduped];
}

export function inferWayOfFishingFromType(fishingMethods = {}, typeOfFishing = "") {
  const normalizedType = normalizePastTripOptionKey(typeOfFishing);
  if (!normalizedType) return "";

  return (
    WAY_OF_FISHING_OPTIONS.find((wayOfFishing) =>
      getTypeOfFishingOptionsForWay(fishingMethods, wayOfFishing).some(
        (option) => normalizePastTripOptionKey(option) === normalizedType
      )
    ) || ""
  );
}

export function buildPastTripStyleOptions(
  fishingMethods = {},
  wayOfFishing = "",
  typeOfFishing = ""
) {
  const methods = fishingMethods?.[wayOfFishing];
  if (!methods || typeof methods !== "object") {
    return [];
  }

  const matchedKey = findCaseInsensitiveOptionKey(methods, typeOfFishing);
  const list = matchedKey ? methods[matchedKey] : [];

  return Array.isArray(list) ? dedupeStringValues(list) : [];
}

export function buildPastTripTechniqueOptions(
  fishingMethods = {},
  wayOfFishing = "",
  typeOfFishing = "",
  style = ""
) {
  const techniquesByWay = fishingMethods?.techniques?.[wayOfFishing];
  if (!techniquesByWay || typeof techniquesByWay !== "object") {
    return [];
  }

  const matchedTypeKey = findCaseInsensitiveOptionKey(techniquesByWay, typeOfFishing);
  const techniquesByType = matchedTypeKey ? techniquesByWay[matchedTypeKey] : null;
  if (!techniquesByType || typeof techniquesByType !== "object") {
    return [];
  }

  const matchedStyleKey = findCaseInsensitiveOptionKey(techniquesByType, style);
  const list = matchedStyleKey ? techniquesByType[matchedStyleKey] : [];

  return Array.isArray(list) ? dedupeStringValues(list) : [];
}

export function buildPastTripBrandModelSuggestions(
  database = [],
  query = "",
  limit = 8
) {
  const normalizedQuery = normalizePastTripOptionKey(query);
  if (!normalizedQuery || !Array.isArray(database)) {
    return [];
  }

  const suggestions = database.flatMap((item) => {
    const displayName =
      trimToUndefined(item?.display_name) ||
      [
        trimToUndefined(item?.brand),
        trimToUndefined(item?.model),
        trimToUndefined(item?.submodel),
        trimToUndefined(item?.buoyancy),
      ]
        .filter(Boolean)
        .join(" ");

    if (!displayName) {
      return [];
    }

    const searchText = [
      trimToUndefined(item?.id),
      trimToUndefined(item?.brand),
      trimToUndefined(item?.model),
      trimToUndefined(item?.submodel),
      trimToUndefined(item?.buoyancy),
      trimToUndefined(item?.display_name),
      displayName,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return searchText.includes(normalizedQuery) ? [displayName] : [];
  });

  return dedupeStringValues(suggestions).slice(0, limit);
}

export function buildPastTripBaitSuggestions({
  typeOfFishing = "",
  baitDatabase = [],
  liveBaitDatabase = [],
  deadBaitDatabase = [],
  query = "",
  limit = 8,
}) {
  const normalizedQuery = normalizePastTripOptionKey(query);
  if (!normalizedQuery) {
    return [];
  }

  let source = baitDatabase;
  const normalizedType = normalizePastTripOptionKey(typeOfFishing);

  if (normalizedType.includes("live bait")) {
    source = liveBaitDatabase;
  } else if (normalizedType.includes("dead bait")) {
    source = deadBaitDatabase;
  }

  const suggestions = (Array.isArray(source) ? source : []).flatMap((item) => {
    if (typeof item === "string") {
      return item.toLowerCase().includes(normalizedQuery) ? [item] : [];
    }

    const name = typeof item?.name === "string" ? item.name : "";
    const base = typeof item?.base === "string" ? item.base : "";
    const category = typeof item?.category === "string" ? item.category : "";

    return [name, base, category]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(normalizedQuery))
      ? [name || base]
      : [];
  });

  return dedupeStringValues(suggestions).slice(0, limit);
}

export function buildPastTripCatchInitialState(trip) {
  return {
    fish: "",
    date: trip?.startDate || trip?.endDate || "",
    time: "",
    size: "",
    weight: "",
    wayOfFishing: "",
    typeOfFishing: trip?.fishingType || "",
    style: "",
    technique: "",
    rod: "",
    reel: "",
    savedGearSetId: "",
    coordinates: "",
    location: trip?.location || "",
    lure: "",
    bait: "",
    notes: "",
  };
}

export function formatTripDateRangeCopy(startDate, endDate) {
  const start = parseTripDate(startDate);
  const end = parseTripDate(endDate || startDate);

  if (!start) return "";

  const format = (value) =>
    value.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  if (!end || toIsoDateString(end) === toIsoDateString(start)) {
    return format(start);
  }

  return `${format(start)} - ${format(end)}`;
}

export function validatePastTripCatchSubmission({
  formState,
  minDate,
  maxDate,
  t,
}) {
  const fish = trimToUndefined(formState?.fish);
  const catchDate = trimToUndefined(formState?.date);
  const coordinates = normalizePastTripCoordinatesInput(formState?.coordinates);

  if (!fish || !catchDate) {
    return t("trip_add_catch_required", {
      defaultValue: "Fish species and catch date are required.",
    });
  }

  if ((minDate && catchDate < minDate) || (maxDate && catchDate > maxDate)) {
    return t("trip_add_catch_date_range_error", {
      defaultValue: "Catch date must fall within the selected trip dates.",
    });
  }

  if (!coordinates.isValid) {
    return t("trip_add_catch_coordinates_invalid", {
      defaultValue: "Enter coordinates as latitude and longitude, for example 42.12345,27.12345.",
    });
  }

  return "";
}

export function buildPastTripCatchPayload({ formState, showLureField }) {
  return {
    fish: trimToUndefined(formState?.fish),
    date: trimToUndefined(formState?.date),
    time: trimToUndefined(formState?.time),
    size: trimToUndefined(formState?.size),
    weight: trimToUndefined(formState?.weight),
    wayOfFishing: trimToUndefined(formState?.wayOfFishing),
    typeOfFishing: trimToUndefined(formState?.typeOfFishing),
    style: trimToUndefined(formState?.style),
    technique: trimToUndefined(formState?.technique),
    rod: trimToUndefined(formState?.rod),
    reel: trimToUndefined(formState?.reel),
    coordinates: trimToUndefined(
      normalizePastTripCoordinatesInput(formState?.coordinates).value
    ),
    lure: showLureField ? trimToUndefined(formState?.lure) : undefined,
    bait: showLureField ? undefined : trimToUndefined(formState?.bait),
    notes: trimToUndefined(formState?.notes),
    location: trimToUndefined(formState?.location),
  };
}
